import type { ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MermaidBlock } from './chat-mermaid';
import { sanitizeChatHtml } from './sanitize';
/* ★ 모양을 렌더러가 «데리고 다닌다» — 쓰는 화면이 따로 CSS 를 챙기지 않아도 된다. */
import './chat-markdown.css';

/**
 * 채팅 메시지 본문용 markdown 렌더러 — react-markdown + remark-gfm (표/체크박스) + Mermaid.
 *
 * 보안: react-markdown 은 기본적으로 dangerouslySetInnerHTML 미사용 (React 가 자동 escape).
 *       Mermaid 는 `securityLevel: 'strict'` 로 raw HTML 차단.
 *
 * 지원 문법:
 *   - **bold** / *italic* / `inline code`
 *   - [text](url) — http/https/mailto 만 (다른 스킴 무력화)
 *   - 헤딩 (#, ##, ###)
 *   - 리스트 (-, *, 1.)
 *   - 인용 (>)
 *   - 표 (| ... |) — remark-gfm
 *   - 체크박스 (- [ ] / - [x]) — remark-gfm
 *   - fenced code (```lang ... ```) — ```mermaid 는 MermaidBlock 렌더링
 *   - 수평선 (---)
 */

function isSafeUrl(url: string | undefined): boolean {
  if (!url) return false;
  return /^https?:\/\//i.test(url) || /^mailto:/i.test(url);
}

// 줄 시작 블록 레벨 HTML 여는 태그 — 답변 본문이 raw HTML 블록임을 가리키는 신호.
const BLOCK_HTML_OPEN_RE =
  /^\s*<(table|thead|tbody|tr|td|th|div|ul|ol|li|details|summary|figure|figcaption|blockquote|section|article|h[1-6])\b/i;

/**
 * 펜스 없는 raw HTML 답변 백스톱 — orchestrator output_format='html' 은 LLM 에게 답변을
 * ```html ... ``` 로 감싸라 지시하지만, LLM 이 펜스를 누락하면(예: 회의록 답변) react-markdown
 * (rehype-raw 미사용)이 HTML 을 escape 해 태그 문자열 그대로 노출된다. 결정론적 답변 캐시가
 * 그 펜스 누락본을 고정시키므로 렌더 계층에서 봉합: 줄 시작에 블록 HTML 여는 태그가 나타나면
 * 그 지점부터 끝까지를 ```html 으로 감싸 기존 sanitizeChatHtml 렌더 경로로 태운다.
 *
 * 무회귀 보장: 이미 어떤 펜스(```)라도 있으면(정상 ```html / ```mermaid / 코드블록) 손대지 않음.
 */
function wrapBareAnswerHtml(content: string): string {
  if (content.includes('```')) return content;
  const lines = content.split('\n');
  const idx = lines.findIndex((l) => BLOCK_HTML_OPEN_RE.test(l));
  if (idx === -1) return content;
  const head = lines.slice(0, idx).join('\n').replace(/\s+$/, '');
  const html = lines.slice(idx).join('\n');
  return `${head ? `${head}\n\n` : ''}\`\`\`html\n${html}\n\`\`\``;
}

/** 줄 시작 펜스(```) 가 홀수 개면 아직 닫히지 않은 블록이 열려 있다는 뜻. */
function hasOpenFence(text: string): boolean {
  const fences = text.match(/^[ \t]*```/gm);
  return !!fences && fences.length % 2 === 1;
}

/** 열려 있는 펜스의 언어에 맞는 안내 문구 — 스트리밍 중 미완성 블록 자리에 놓는다. */
function pendingNoteFor(fenceLine: string): string {
  const lang = /^[ \t]*```([\w-]*)/.exec(fenceLine)?.[1]?.toLowerCase() ?? '';
  if (lang === 'html') return '표를 그리는 중…';
  if (lang === 'mermaid') return '다이어그램을 그리는 중…';
  return '작성 중…';
}

/**
 * 스트리밍 중인 AI 답변 렌더 — 도착한 만큼만 renderMessageMarkdown 으로 그린다.
 *
 * 왜 별도 함수인가: 스트리밍 말풍선은 예전에 텍스트를 **날것 그대로** 출력했다. 답변이
 * ```html ... ``` 표(orchestrator output_format='html')면 도착하는 동안 `<table style=...>`
 * 태그가 그대로 보였다. 그렇다고 매 chunk 를 그냥 markdown 으로 태우면 **아직 닫히지 않은**
 * 펜스 안의 반쪽 HTML 이 정화·자동닫힘을 거쳐 표가 덜컹거리며 자란다.
 *
 * 그래서 열린 펜스는 **문구 하나로 가리고**(표를 그리는 중…) 그 앞의 완성된 부분만 그린다.
 * 스트림이 끝나면(isStreaming=false) 가리지 않으므로 최종 메시지와 같은 모습이 된다.
 */
export function renderStreamingMarkdown(text: string, isStreaming: boolean): ReactNode {
  if (!text) return text;
  if (!isStreaming || !hasOpenFence(text)) return renderMessageMarkdown(text);
  const lines = text.split('\n');
  let cut = lines.length - 1;
  for (let i = lines.length - 1; i >= 0; i -= 1) {
    if (/^[ \t]*```/.test(lines[i] ?? '')) { cut = i; break; }
  }
  const head = lines.slice(0, cut).join('\n').replace(/\s+$/, '');
  return (
    <>
      {head ? renderMessageMarkdown(head) : null}
      <span className="chat-md-pending">{pendingNoteFor(lines[cut] ?? '')}</span>
    </>
  );
}

export function renderMessageMarkdown(content: string): ReactNode {
  if (!content) return content;
  return (
    <div className="chat-md">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a({ href, children }) {
            if (!isSafeUrl(href)) return <>{children}</>;
            return (
              <a href={href} target="_blank" rel="noopener noreferrer nofollow">
                {children}
              </a>
            );
          },
          code(props) {
            const { className, children } = props;
            const lang = /language-([\w-]+)/.exec(className || '')?.[1];
            const raw = String(children ?? '').replace(/\n$/, '');
            // mermaid 전용 fenced block
            if (lang === 'mermaid') {
              return <MermaidBlock code={raw} />;
            }
            // html fenced block — DOMPurify(style 허용) 로 정화 후 렌더(답변 포맷 색상 표 등).
            if (lang === 'html') {
              return <div className="chat-md-html" dangerouslySetInnerHTML={{ __html: sanitizeChatHtml(raw) }} />;
            }
            // 인라인 code 와 fenced code 모두 className=language-... 가 없으면 inline.
            const isInline = !lang && !raw.includes('\n');
            if (isInline) {
              return <code className="chat-md-code">{children}</code>;
            }
            return (
              <pre className="chat-md-pre">
                <code className={className}>{children}</code>
              </pre>
            );
          },
          ul({ children }) { return <ul className="chat-md-list">{children}</ul>; },
          ol({ children }) { return <ol className="chat-md-list chat-md-list--ordered">{children}</ol>; },
          h1({ children }) { return <h3 className="chat-md-h1">{children}</h3>; },
          h2({ children }) { return <h4 className="chat-md-h2">{children}</h4>; },
          h3({ children }) { return <h5 className="chat-md-h3">{children}</h5>; },
          blockquote({ children }) { return <blockquote className="chat-md-quote">{children}</blockquote>; },
          table({ children }) { return <div className="chat-md-table-wrap"><table className="chat-md-table">{children}</table></div>; },
          hr() { return <hr className="chat-md-hr" />; },
        }}
      >
        {wrapBareAnswerHtml(content)}
      </ReactMarkdown>
    </div>
  );
}
